import { DateTime } from 'luxon';
import { CalendarDayInfo } from '../CalendarDay/calendar-day-info';

interface CalendarDayDateArgs {
    selected?: Date;
    selectedTo?: Date;
    hovered?: Date;
    isSelectingTo?: boolean;
}

function isSameDay(first?: Date, second?: Date): boolean {
    return !!first && !!second && DateTime.fromJSDate(first).hasSame(DateTime.fromJSDate(second), 'day');
}

function isSameDayOrBefore(first?: Date, second?: Date): boolean {
    return !!first && !!second && (isSameDay(first, second) || first < second);
}

function isSameDayOrAfter(first?: Date, second?: Date): boolean {
    return !!first && !!second && (isSameDay(first, second) || first > second);
}

function getIsBetweenHovered(date: Date, args: CalendarDayDateArgs): boolean {
    const { hovered: hoveredDate, isSelectingTo, selected, selectedTo } = args;
    const isBetweenHoveredToCase =
        !!hoveredDate &&
        isSelectingTo &&
        isSameDayOrAfter(date, selectedTo ?? selected) &&
        isSameDayOrBefore(date, hoveredDate);
    const isBetweenHoveredFromCase =
        !!hoveredDate &&
        !!selectedTo &&
        !!selected &&
        !isSelectingTo &&
        isSameDayOrBefore(date, selected) &&
        isSameDayOrAfter(date, hoveredDate);

    return isBetweenHoveredToCase || isBetweenHoveredFromCase;
}

function getIsHoveredStart(date: Date, args: CalendarDayDateArgs, isBetweenHovered: boolean) {
    const { hovered: hoveredDate, isSelectingTo, selected, selectedTo } = args;
    const isHoveredStartFromCase = isBetweenHovered && !isSelectingTo && isSameDay(date, hoveredDate);
    const isHoveredStartToCase = isBetweenHovered && isSelectingTo && isSameDay(date, selectedTo ?? selected);
    return isHoveredStartFromCase || isHoveredStartToCase;
}

function getIsHoveredEnd(date: Date, args: CalendarDayDateArgs, isBetweenHovered: boolean) {
    const { hovered: hoveredDate, isSelectingTo, selected, selectedTo } = args;
    const isHoveredStartFromCase =
        isBetweenHovered && !isSelectingTo && !!selectedTo && !!selected && isSameDay(date, selected);
    const isHoveredStartToCase = isBetweenHovered && isSelectingTo && !!selected && isSameDay(date, hoveredDate);
    return isHoveredStartFromCase || isHoveredStartToCase;
}

export function getHoveredStatesForDate(
    date: Date,
    args: CalendarDayDateArgs,
): Pick<CalendarDayInfo, 'isBetweenHovered' | 'isHovered' | 'isHoveredEnd' | 'isHoveredStart'> {
    const { hovered: hoveredDate, isSelectingTo, selected, selectedTo } = args;
    const isBetweenHovered = getIsBetweenHovered(date, {
        hovered: hoveredDate,
        isSelectingTo,
        selected,
        selectedTo,
    });
    const isHoveredStart = getIsHoveredStart(
        date,
        { hovered: hoveredDate, isSelectingTo, selected, selectedTo },
        isBetweenHovered,
    );
    const isHoveredEnd = getIsHoveredEnd(
        date,
        { hovered: hoveredDate, isSelectingTo, selected, selectedTo },
        isBetweenHovered,
    );
    return {
        isBetweenHovered,
        isHovered: !!hoveredDate && isSameDay(hoveredDate, date),
        isHoveredEnd,
        isHoveredStart,
    };
}

export function useCalendarDays(dates: Date[], displayMonth: Date, args: CalendarDayDateArgs): CalendarDayInfo[] {
    const { hovered, isSelectingTo, selected, selectedTo } = args;

    return dates.map((date, index) => {
        const isFrom = isSameDay(date, selected);
        const isTo = isSameDay(date, selectedTo);
        return {
            date,
            isBetweenSelected: isSameDayOrAfter(date, selected) && isSameDayOrBefore(date, selectedTo),
            isCurrentMonth: DateTime.fromJSDate(date).hasSame(DateTime.fromJSDate(displayMonth), 'month'),
            isFirstDayOfWeek: index % 7 === 0,
            isLastDayOfWeek: index % 7 === 6,
            isSelected: isFrom,
            isSelectedTo: isTo,
            ...getHoveredStatesForDate(date, {
                hovered,
                isSelectingTo,
                selected,
                selectedTo,
            }),
        };
    });
}
